import React, { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

const ROLES = ["Designer", "Developer", "Writer", "Marketer", "Analyst", "Consultant", "Other"];

export default function CTASection() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(ROLES[0]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/waitlist", { email, role });
      setDone(true);
    } catch {
      setDone(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" data-testid="cta-section" className="relative py-24 scroll-mt-20">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900 p-10 md:p-16 text-white">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-sky-400/10 blur-3xl" />
          <div className="absolute inset-0 bg-grid opacity-[0.08]" />

          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-300/40 bg-blue-500/15 px-3 py-1 text-xs font-medium text-blue-100">
                <Sparkles size={14} /> Founding members get lifetime ₹0 platform fee on their first gig
              </span>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-tight">
                Your career, working <span className="text-blue-300">on autopilot.</span>
              </h2>
              <p className="mt-4 text-slate-300 max-w-md">
                Join thousands of freelancers turning idle resumes into income — paid in ₹, straight to UPI.
              </p>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex -space-x-2 shrink-0">
                  {["AnanyaS", "RohitV", "MeeraI", "DevonOrr2", "MayaCarter9"].map((s) => (
                    <img
                      key={s}
                      src={`https://api.dicebear.com/7.x/notionists/svg?seed=${s}&backgroundColor=dbeafe,bfdbfe,e0f2fe&radius=50`}
                      alt={s}
                      className="w-8 h-8 sm:w-9 sm:h-9 rounded-full ring-2 ring-slate-900"
                    />
                  ))}
                </div>
                <p className="text-sm text-slate-300">
                  <span className="font-semibold text-white">1,200+</span> freelancers already on the list
                </p>
              </div>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-3" data-testid="waitlist-form">
              {!done ? (
                <>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">I work as a</p>
                    <div className="flex flex-wrap gap-1.5" data-testid="waitlist-role-chips">
                      {ROLES.map((r) => (
                        <button
                          type="button"
                          key={r}
                          onClick={() => setRole(r)}
                          data-testid={`waitlist-role-${r.toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-sm border transition ${
                            role === r
                              ? "bg-blue-500 border-blue-500 text-white"
                              : "bg-white/5 border-white/15 text-slate-200 hover:bg-white/10"
                          }`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@work-email.com"
                      data-testid="waitlist-email"
                      className="flex-1 rounded-md bg-white/95 text-slate-900 placeholder-slate-400 px-4 py-3.5 outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="waitlist-submit"
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-500 hover:bg-blue-400 text-white font-medium px-5 py-3.5 transition"
                    >
                      {loading ? "Joining…" : <>Join waitlist <ArrowRight size={16} /></>}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">Free to join. ₹0 upfront. Cancel anytime.</p>
                </>
              ) : (
                <div data-testid="waitlist-success" className="flex items-start gap-3 rounded-md bg-emerald-500/15 border border-emerald-400/40 px-4 py-5">
                  <CheckCircle2 className="text-emerald-300 mt-0.5" />
                  <div>
                    <p className="text-emerald-50 font-display font-semibold">You&apos;re on the list.</p>
                    <p className="text-emerald-100/80 text-sm mt-1">We&apos;ll email you at <span className="text-white font-medium">{email}</span> when your founding-member slot opens.</p>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
